import { OutlookInstance, HxModuleIdentifier } from '@outlook/hxcore_web';
import { installAccountEventsHandler } from 'hx-account-events';
import { refreshAllAccountAuthTokens } from 'hx-account';
import { initializeHxIdMap } from 'hx-id-map';
import { initializeHxHealth } from 'hx-health';
import { handleHxObjectLoadResult } from 'hx-object-load-helpers';

/**
 * Initializes side-effects for hx availability
 * (e.g. account management event handlers for webview2,
 * auth token availability, etc)
 */
export async function initializeHx(outlookInstance: OutlookInstance) {
    // initialize hx health delegate before we make any hx requests
    initializeHxHealth(outlookInstance);

    // install webview2 account event handlers
    installAccountEventsHandler();

    // initialize hx id mapping before we start loading hx objects
    initializeHxIdMap();

    // notify hx that we have booted and hx has been loaded
    // TODO (#88364) move this out of common hx-init code and make this
    // behaviour depend on the module you are in.
    outlookInstance.actorAPIs.moduleActivated({
        module: HxModuleIdentifier.Mail,
    });

    // This call also triggers hx to start syncing.
    //
    // TODO (#88365) Defer syncing until the app is "booted enough" that we're
    // not contending for the network with hx sync.
    outlookInstance.actorAPIs.onBootCompleted();

    // refresh auth tokens and add an account if we haven't
    // already synced one.
    //
    // TODO (#88366) Remove this once the native host has account support
    // and we no longer need to manage auth in the hosted window.

    const sessionId = outlookInstance.activeSet.createSession();
    try {
        await refreshAllAccountAuthTokens(sessionId);
    } finally {
        outlookInstance.activeSet.releaseSession(sessionId);
    }

    // Set the Server Undo state to be true for this outlookInstance
    // so that all the undo-able actions taken will propogate to server directly
    outlookInstance.actorAPIs.setServerUndoState({
        value: true,
    });

    // Hx appears to have a bug where it doesn't flush during initial sync if the app stays in the foreground.
    // Call setIsWorkingOffline as a workaround for now since it is harmless and causes hx to flush
    //
    // TODO (#88130) remove this hack once the hx bug is resolved
    setTimeout(() => forceFlushAllAccounts(outlookInstance), 15000);

    // When we unload the window, suspend hx core.
    // TODO (#88133) This may be better handled outside the main window, especially if we intend to support
    // multi-window. Consider moving this to a SharedWorker, or having the native host manage the suspended
    // state of HxCore
    //
    // This may also be the root of the bug where reloading the window breaks our connection to HxCore (#88134).
    window.addEventListener('unload', function (event) {
        outlookInstance.actorAPIs.prepareToSuspendHxCore();
    });
}

/**
 * Gets all accounts syncing mail, and sets them as working online
 */
async function forceFlushAllAccounts(hx: OutlookInstance) {
    const activeSet = hx.activeSet;
    const sessionId = activeSet.createSession();
    const root = await activeSet.getRoot(sessionId);
    const hxAccounts = handleHxObjectLoadResult(
        await root.getAccountsSyncingMailOrCalendar(sessionId)
    );
    hxAccounts.items.forEach(hxAccount => {
        hx.actorAPIs.setIsWorkingOffline([hxAccount.objectId], {
            value: false,
        });
    });
    activeSet.releaseSession(sessionId);
}
